import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ImageIcon, Check, X } from 'lucide-react';
import { DOOR_STYLE_KEYS } from '@/data/palette';

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-neutral-100">
    <span className="text-[13px] uppercase tracking-wider font-semibold text-neutral-500">
      {label}
    </span>
    <span className="text-[14px] text-neutral-800 text-right">{value}</span>
  </div>
);

const ColorModal = ({ item, open, onOpenChange }) => {
  const contentRef = useRef(null);

  // When modal opens, ensure it scrolls itself into the visible viewport.
  // Helps when the iframe is taller than the user's actual screen viewport.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const node = contentRef.current;
      if (node && typeof node.scrollIntoView === 'function') {
        try {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {
          node.scrollIntoView();
        }
      }
    }, 80);
    return () => clearTimeout(t);
  }, [open, item]);

  if (!item) return null;

  const doorEntries = Object.entries(DOOR_STYLE_KEYS);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} className="max-w-3xl p-0 overflow-hidden bg-white rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Image / swatch */}
          <div className="relative aspect-square md:aspect-auto md:min-h-[420px] bg-gradient-to-br from-neutral-200 to-neutral-300 flex items-center justify-center overflow-hidden">
            {item.image ? (
              <img
                src={item.image}
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : item.swatchColor ? (
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: item.swatchColor,
                  border: item.swatchBorder ? `1px solid ${item.swatchBorder}` : undefined,
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <>
                <ImageIcon className="w-20 h-20 text-neutral-400" strokeWidth={1} />
                <span className="absolute bottom-3 left-3 text-[11px] uppercase tracking-wider text-neutral-500 bg-white/80 px-2 py-0.5 rounded">
                  Image coming soon
                </span>
              </>
            )}
          </div>

          {/* Details */}
          <div className="p-6 md:p-8 max-h-[80vh] overflow-y-auto">
            <DialogHeader className="text-left mb-4">
              <p className="text-[12px] uppercase tracking-[0.18em] text-[#6BA797] font-semibold mb-1">
                {item.manufacturer}
              </p>
              <DialogTitle className="text-[26px] font-serif text-neutral-900 leading-tight">
                {item.name}
              </DialogTitle>
              <p className="text-[13px] text-neutral-500 mt-1">Code: {item.code}</p>
            </DialogHeader>

            {item.features.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {item.features.map((f) => (
                  <Badge
                    key={f}
                    variant="outline"
                    className="border-[#6BA797] text-[#4f8676] bg-[#6BA797]/10 text-[11px] uppercase tracking-wider font-semibold rounded-sm"
                  >
                    {f}
                  </Badge>
                ))}
              </div>
            )}

            <div className="space-y-0">
              <DetailRow label="Color Family" value={item.family} />
              <DetailRow label="Pattern" value={item.pattern} />
              <DetailRow label="Texture" value={item.texture} />
              <DetailRow label="Price" value={item.price} />
            </div>

            <h4 className="mt-5 mb-2 text-[13px] uppercase tracking-wider font-semibold text-neutral-500">
              Available Door &amp; Drawer Styles
            </h4>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {doorEntries.map(([label, key]) => {
                const yes = !!item.doors[key];
                return (
                  <li
                    key={key}
                    className="flex items-center gap-2 text-[13px] text-neutral-700"
                  >
                    {yes ? (
                      <Check className="w-4 h-4 text-[#6BA797] shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-neutral-300 shrink-0" />
                    )}
                    <span className={yes ? '' : 'text-neutral-400 line-through'}>
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ColorModal;
